import { Tooltip, Typography } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import type {
	BottleneckRow,
	BottleneckSegment,
} from '../../../../redux/api/bottleneck-report';
import {
	BUCKET_COLORS,
	OVERDUE_COLOR,
	SEGMENT_LABELS,
	formatDuration,
} from './buckets';

const { Text } = Typography;

const BAR_HEIGHT = 10;

interface JobRowProps {
	row: BottleneckRow;
	/** Board-wide length that fills the elapsed half of the track. */
	elapsedScale: number;
	/** Board-wide length that fills the runway half of the track. */
	remainingScale: number;
	/** Revenue is only in the payload when the viewer may see it. */
	showRevenue: boolean;
}

const isElapsed = (segment: BottleneckSegment) =>
	segment.bucket !== 'remaining';

/**
 * One line item as a single scannable line: what it is, when it is due, and a
 * bar of where its life has gone.
 *
 * The bar is split at "now" -- history to the left, runway to the right -- so
 * a row can be read against its neighbours instead of only against itself.
 */
const JobRow = ({
	row,
	elapsedScale,
	remainingScale,
	showRevenue,
}: JobRowProps) => {
	const elapsed = row.segments.filter(isElapsed);
	const remaining = row.segments.find((segment) => !isElapsed(segment));

	const elapsedTotal = elapsed.reduce(
		(total, segment) => total + segment.seconds,
		0
	);
	// A job past the scale keeps its full bar and gets flagged, rather than
	// being clipped or allowed to redraw every other row.
	const elapsedOffScale = elapsedTotal > elapsedScale;
	const elapsedDivisor = Math.max(elapsedTotal, elapsedScale);
	const remainingOffScale = (remaining?.seconds ?? 0) > remainingScale;
	const remainingDivisor = Math.max(remaining?.seconds ?? 0, remainingScale);

	const dueDate = row.shop_due_date ?? row.required_part_ship_date;
	const ticketCount = row.open_ticket_count ?? 0;

	const tooltip = (
		<div style={{ maxWidth: 280 }}>
			<div style={{ fontWeight: 600 }}>{row.order_name}</div>
			<div style={{ marginBottom: 6 }}>{row.name}</div>
			{/*
			 * Above the timings rather than below them: a job with an open
			 * ticket is not waiting on the stage it appears to be in, so this
			 * changes how everything under it should be read.
			 */}
			{ticketCount > 0 && (
				<div style={{ marginBottom: 6 }}>
					<WarningFilled /> {ticketCount === 1 ? 'Open ticket' : 'Open tickets'}
					{row.open_ticket_uids?.length
						? `: ${row.open_ticket_uids.join(', ')}`
						: ''}
				</div>
			)}
			{elapsed.map((segment, index) => (
				<div
					key={`${segment.bucket}-${index}`}
					style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}
				>
					<span>
						<span
							style={{
								display: 'inline-block',
								width: 8,
								height: 8,
								marginRight: 6,
								borderRadius: 2,
								background: BUCKET_COLORS[segment.bucket],
							}}
						/>
						{SEGMENT_LABELS[segment.bucket]}
						{segment.open ? ' (now)' : ''}
					</span>
					<span>{formatDuration(segment.seconds)}</span>
				</div>
			))}
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					gap: 16,
					marginTop: 6,
					borderTop: '1px solid rgba(255,255,255,0.25)',
					paddingTop: 4,
				}}
			>
				<span>Total age</span>
				<span>{formatDuration(elapsedTotal)}</span>
			</div>
			{remaining && (
				<div
					style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}
				>
					<span>{remaining.overdue ? 'Past due by' : 'Time remaining'}</span>
					<span>{formatDuration(remaining.seconds)}</span>
				</div>
			)}
			<div style={{ marginTop: 6 }}>
				Due {dueDate ? dayjs(dueDate).format('MMM D, YYYY') : 'not set'} ·{' '}
				{row.quantity.toLocaleString()} parts
			</div>
		</div>
	);

	return (
		<Tooltip title={tooltip} mouseEnterDelay={0.3} placement="topLeft">
			<Link
				to={`/orders/line-items/${row.id}`}
				className="flex items-center gap-2 px-2 py-1 text-xs text-inherit hover:bg-neutral-100"
			>
				<span className="w-44 shrink-0 truncate font-medium">
					{row.order_name || '—'}
				</span>
				<span className="w-40 shrink-0 truncate text-neutral-500">
					{row.name}
				</span>
				<span className="w-14 shrink-0 text-right tabular-nums text-neutral-500">
					{row.quantity.toLocaleString()}
				</span>
				{showRevenue && (
					<span className="w-20 shrink-0 text-right tabular-nums text-neutral-500">
						{row.revenue ? Number(row.revenue).toLocaleString() : '—'}
					</span>
				)}
				<span
					className={`w-20 shrink-0 text-right tabular-nums ${
						row.is_overdue ? 'text-red-500' : 'text-neutral-500'
					}`}
				>
					{dueDate ? dayjs(dueDate).format('MMM D') : '—'}
				</span>

				<span className="flex min-w-0 flex-1 items-center">
					{/*
					 * The markers sit outside the tracks, in a slot that is always
					 * reserved. Inside a `justify-end overflow-hidden` track the
					 * segments already fill 100% when off-scale, so a marker placed
					 * there overflows the start edge and is clipped exactly when it
					 * has something to say -- and showing it would shift the bar.
					 */}
					<span
						className="w-2 shrink-0 text-right text-[9px] leading-none text-neutral-400"
						aria-hidden={!elapsedOffScale}
					>
						{elapsedOffScale ? '\u00ab' : ''}
					</span>
					<span className="flex flex-1 justify-end overflow-hidden">
						{elapsed.map((segment, index) => (
							<span
								key={`${segment.bucket}-${index}`}
								className="shrink-0"
								style={{
									width: `${(segment.seconds / elapsedDivisor) * 100}%`,
									height: BAR_HEIGHT,
									background: BUCKET_COLORS[segment.bucket],
								}}
							/>
						))}
					</span>
					<span
						className="shrink-0 bg-neutral-600"
						style={{ width: 2, height: BAR_HEIGHT + 8 }}
					/>
					<span className="flex flex-1 overflow-hidden">
						{remaining && (
							<span
								className="shrink-0"
								style={{
									width: `${(remaining.seconds / remainingDivisor) * 100}%`,
									height: BAR_HEIGHT,
									background: remaining.overdue
										? OVERDUE_COLOR
										: BUCKET_COLORS.remaining,
								}}
							/>
						)}
					</span>
					<span
						className="w-2 shrink-0 text-[9px] leading-none text-neutral-400"
						aria-hidden={!remainingOffScale}
					>
						{remainingOffScale ? '\u00bb' : ''}
					</span>
				</span>

				{/*
				 * The slot is always reserved even when clean, so the "In stage"
				 * column below it stays in one place down the whole section --
				 * a ragged right edge is harder to scan than a mostly-empty
				 * column.
				 */}
				<span
					className="w-8 shrink-0 whitespace-nowrap text-center text-[11px] leading-none text-amber-500"
					data-testid={ticketCount ? 'job-row-ticket-flag' : undefined}
				>
					{ticketCount > 0 && (
						<>
							<WarningFilled />
							{ticketCount > 1 && (
								<span className="ml-0.5 tabular-nums">{ticketCount}</span>
							)}
						</>
					)}
				</span>

				<span className="w-16 shrink-0 whitespace-nowrap text-right tabular-nums">
					<Text type={row.is_overdue ? 'danger' : undefined}>
						{formatDuration(row.current_dwell_seconds)}
					</Text>
				</span>
			</Link>
		</Tooltip>
	);
};

export default JobRow;
